import type { JSX } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { GuestOnly, RequireAuth } from './auth/RequireAuth.js';
import { AppLayout } from './pages/AppLayout.js';
import { BooksPage } from './pages/BooksPage.js';
import { BookBuilderPage } from './pages/BookBuilderPage.js';
import { BookPreviewPage } from './pages/BookPreviewPage.js';
import { DemoBookPage } from './pages/DemoBookPage.js';
import { PublicBookPage } from './pages/PublicBookPage.js';
import { CharactersPage } from './pages/CharactersPage.js';
import { LocationsPage } from './pages/LocationsPage.js';
import { ThingsPage } from './pages/ThingsPage.js';
import { SupportCharacterPage } from './pages/SupportCharacterPage.js';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage.js';
import { StorefrontPage } from './pages/StorefrontPage.js';
import { ScrollToTop } from './components/ScrollToTop.js';
import { LoginPage } from './pages/LoginPage.js';
import { RegisterPage } from './pages/RegisterPage.js';
import { RenderShowcasePage } from './pages/RenderShowcasePage.js';
import { ResetPasswordPage } from './pages/ResetPasswordPage.js';
import { ReportPage } from './pages/ReportPage.js';
import { VerifyEmailPage } from './pages/VerifyEmailPage.js';
import { AdminGenerationsPage } from './pages/AdminGenerationsPage.js';
import { AdminAccountsPage } from './pages/AdminAccountsPage.js';
import { AdminUsagePage } from './pages/AdminUsagePage.js';
import { AdminBookBuilderPage } from './pages/AdminBookBuilderPage.js';
import { AdminPromotionsPage } from './pages/AdminPromotionsPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { LegalDocumentPage, LegalIndexPage } from './pages/LegalPage.js';

/**
 * Карта маршрутов. Лендинг, демо-книга и книга по публичной ссылке открыты
 * гостю (E15, §3.16), всё под `/app` — только вошедшим.
 */
export function App(): JSX.Element {
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<StorefrontPage page="landing" />} />
        <Route path="/pricing" element={<StorefrontPage page="pricing" />} />
        <Route path="/demo" element={<DemoBookPage />} />
        <Route path="/p/:token" element={<PublicBookPage />} />
        {/* Guest route: the complaint channel is for people who have no account (§3.5). */}
        <Route path="/report" element={<ReportPage />} />
        <Route
          path="/login"
          element={
            <GuestOnly>
              <LoginPage />
            </GuestOnly>
          }
        />
        <Route
          path="/register"
          element={
            <GuestOnly>
              <RegisterPage />
            </GuestOnly>
          }
        />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/legal" element={<LegalIndexPage />} />
        <Route path="/legal/:document" element={<LegalDocumentPage />} />
        <Route path="/__render-showcase" element={<RenderShowcasePage />} />
        <Route
          path="/admin/generations"
          element={
            <RequireAuth>
              <AdminGenerationsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/accounts"
          element={
            <RequireAuth>
              <AdminAccountsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/usage"
          element={
            <RequireAuth>
              <AdminUsagePage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/promotions"
          element={
            <RequireAuth>
              <AdminPromotionsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/book-builder"
          element={
            <RequireAuth>
              <AdminBookBuilderPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/accounts/:ownerId/books/:id/manual"
          element={
            <RequireAuth>
              <AdminBookBuilderPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/accounts/:ownerId/books/:id"
          element={
            <RequireAuth>
              <BookBuilderPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/accounts/:ownerId/books/:id/preview"
          element={
            <RequireAuth>
              <BookPreviewPage />
            </RequireAuth>
          }
        />
        <Route
          path="/app/books/:id/preview"
          element={
            <RequireAuth>
              <BookPreviewPage />
            </RequireAuth>
          }
        />

        <Route
          path="/app"
          element={
            <RequireAuth>
              <AppLayout />
            </RequireAuth>
          }
        >
          <Route index element={<BooksPage />} />
          <Route path="books/:id" element={<BookBuilderPage />} />
          <Route path="characters" element={<CharactersPage />} />
          <Route path="things" element={<ThingsPage />} />
          <Route path="locations" element={<LocationsPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="support/characters/:id" element={<SupportCharacterPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
